import { AdminOnlyRing } from 'components/AdminOnlyRing'
import { FreeAccessBadge } from 'components/FreeAccessBadge'
import { LibraryObjectBadge } from 'components/LibraryObjectBadge'
import { useStudentAssignmentCubit } from 'hooks/cubits/studentAssignment'
import { useBreakoutUser } from 'hooks/profile'
import { useRootStore } from 'hooks/rootStore'
import { useLTIMode } from 'hooks/use-lti-mode'
import { observer } from 'mobx-react-lite'
import { Headline } from 'pages/layout/TwoColumn'
import { useCallback, useMemo } from 'react'
import { AdminOnlyActions } from './AdminOnlyActions'
import { useTranslationTyped } from 'i18n/i18n'

export const StudentAssignmentHeader = observer(
  function StudentAssignmentHeader() {
    const cubit = useStudentAssignmentCubit()
    const store = useRootStore()
    const user = useBreakoutUser()
    const { ltiMode } = useLTIMode()
    const { isCorre } = useBreakoutUser()
    const { tt } = useTranslationTyped()

    const instructorAssignmentId =
      store.router.queryParams &&
      typeof store.router.queryParams.backToInstructorAssignment === 'string'
        ? decodeURIComponent(
            store.router.queryParams.backToInstructorAssignment
          )
        : undefined

    const onClick = useCallback(() => {
      if (
        (user.isFaculty || user.isInternal) &&
        typeof instructorAssignmentId === 'string' &&
        instructorAssignmentId.includes('+')
      ) {
        const [sectionId, assignmentId] = instructorAssignmentId.split('+')
        return store.navigateToInstructorAssignmentPage(
          sectionId,
          assignmentId,
          { tab: 'students' }
        )
      }
      store.navigateTo('home', undefined, {
        restoreScroll: 'true',
      })
    }, [store, user.isFaculty, user.isInternal, instructorAssignmentId])

    const label = `${cubit.slideDeck.data.slideDeckName} | ${cubit.slideDeck.data.slideDeckTeaser}`

    const breadcrumbs = useMemo(() => {
      if (ltiMode) return undefined

      if (instructorAssignmentId) {
        return [
          {
            label: tt.instructor_classes.headline(),
            onClick: () => {
              store.navigateToInstructorClassesPage()
            },
          },
          {
            label: cubit.section.sectionLabelWithClass,
            onClick: () => {
              store.navigateToInstructorClassPage(cubit.sectionId)
            },
          },
          {
            label: cubit.assignment.slideDeck.data.slideDeckName,
            onClick: () => {
              store.navigateToInstructorAssignmentPage(
                cubit.sectionId,
                cubit.assignmentId,
                { tab: 'students' }
              )
            },
          },
          {
            label: label,
          },
        ]
      }

      return [
        {
          label: tt.library.breadcrumb(),
          onClick: onClick,
        },
        {
          label: label,
        },
      ]
    }, [
      ltiMode,
      instructorAssignmentId,
      tt,
      onClick,
      label,
      cubit.section.sectionLabelWithClass,
      cubit.assignment.slideDeck.data.slideDeckName,
      cubit.sectionId,
      cubit.assignmentId,
      store,
    ])

    return (
      <div className="mb-3 flex grow-0 flex-col items-center justify-between md:flex-row">
        <Headline
          breadcrumbs={breadcrumbs}
          label={label}
          className="cursor-default truncate text-headline-large antialiased"
        />
        <div className="mt-3 flex flex-row items-center justify-between gap-2 md:mt-0">
          <LibraryObjectBadge libraryObject={cubit.libraryObject} />
          <FreeAccessBadge section={cubit.libraryObject.section} />
          <AdminOnlyRing label={tt.roles.corre_only()} onlyShowIf={isCorre}>
            <AdminOnlyActions />
          </AdminOnlyRing>
        </div>
      </div>
    )
  }
)
